import type { SearchConversationFilters } from '@/entities/search';
import type { ContentSearchMode } from '@/entities/settings';

/**
 * Поля поиска по содержимому диалога — общие у опций, из которых собирается запрос, и у отбора,
 * который в него ушёл. Читать их обязаны одним правилом: иначе тот, кто разбирает готовый отбор
 * (шапка выгрузки), разойдётся с тем, кто его собрал.
 */
export type ConversationContentSearchFields = Pick<
  SearchConversationFilters,
  'phraseSearch' | 'commentSearch' | 'commentSearchActive'
>;

/**
 * Режим поиска по комментариям активен. Он держится и с пустым запросом — по явному признаку:
 * тогда отбор идёт по наличию комментария, а фрагментом приходит последний оставленный.
 */
export const isCommentSearchActive = (search: ConversationContentSearchFields | undefined): boolean =>
  Boolean(search?.commentSearch?.trim()) || search?.commentSearchActive === true;

/**
 * Режим поиска по содержимому, действующий в этом отборе; `null` — поиск по содержимому не идёт.
 * Режимы взаимоисключающие: поиск по репликам отбирает только с непустым запросом, поиск по
 * комментариям — пока активен.
 */
export const readContentSearchMode = (
  search: ConversationContentSearchFields | undefined,
): ContentSearchMode | null => {
  if (search?.phraseSearch?.trim()) return 'phrase';
  return isCommentSearchActive(search) ? 'comment' : null;
};

/**
 * Запрос поиска по содержимому непустой. Только тогда выдача считает релевантность — и только
 * тогда колонка релевантности имеет значения, а сортировка по ней принимается.
 */
export const hasContentSearchQuery = (search: ConversationContentSearchFields | undefined): boolean =>
  Boolean(search?.phraseSearch?.trim()) || Boolean(search?.commentSearch?.trim());
